/**
 * CORP//LLM — Multi-Provider OAuth Authentication
 * Supports Google (PKCE), GitHub, and Discord.
 * Uses Web Crypto API only — no external dependencies.
 */

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string;
const GITHUB_CLIENT_ID = import.meta.env.VITE_GITHUB_CLIENT_ID as string;
const DISCORD_CLIENT_ID = import.meta.env.VITE_DISCORD_CLIENT_ID as string;
const JWT_KEY = 'corpllm_jwt';
const VERIFIER_KEY = 'corpllm_cv';
const OAUTH_PROVIDER_KEY = 'corpllm_oauth_provider';

export interface JwtPayload { sub: string; email: string; name: string; exp: number; admin?: boolean; beta?: boolean; }

export function getJwt(): string | null { return localStorage.getItem(JWT_KEY); }
export function setJwt(t: string): void { localStorage.setItem(JWT_KEY, t); }
export function clearJwt(): void { localStorage.removeItem(JWT_KEY); }

// Parse JWT claims (no validation needed — server already validated)
export function parseJwt(token: string): JwtPayload | null {
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g,'+').replace(/_/g,'/')));
  } catch { return null; }
}

// Generate PKCE code verifier (random 43-128 char URL-safe string)
function genVerifier(): string {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return btoa(String.fromCharCode(...arr)).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,'');
}

// SHA-256 hash → base64url
async function sha256b64(s: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,'');
}

// Random state for CSRF protection
function genState(): string {
  return crypto.randomUUID();
}

// ── Google OAuth (PKCE) ─────────────────────────────────────────────────

export async function startGoogleLogin(): Promise<void> {
  const verifier = genVerifier();
  const challenge = await sha256b64(verifier);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(OAUTH_PROVIDER_KEY, 'google');
  const p = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: location.origin,
    response_type: 'code',
    scope: 'openid email profile',
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state: genState(),
  });
  location.href = `https://accounts.google.com/o/oauth2/v2/auth?${p}`;
}

// ── GitHub OAuth ────────────────────────────────────────────────────────

export function startGitHubLogin(): void {
  sessionStorage.setItem(OAUTH_PROVIDER_KEY, 'github');
  const p = new URLSearchParams({
    client_id: GITHUB_CLIENT_ID,
    redirect_uri: location.origin,
    scope: 'user:email',
    state: genState(),
  });
  location.href = `https://github.com/login/oauth/authorize?${p}`;
}

// ── Discord OAuth ───────────────────────────────────────────────────────

export function startDiscordLogin(): void {
  sessionStorage.setItem(OAUTH_PROVIDER_KEY, 'discord');
  const p = new URLSearchParams({
    client_id: DISCORD_CLIENT_ID,
    redirect_uri: location.origin,
    response_type: 'code',
    scope: 'identify email',
    state: genState(),
  });
  location.href = `https://discord.com/oauth2/authorize?${p}`;
}

// ── Callback Handler (multi-provider) ───────────────────────────────────

export async function handleCallback(serverBase: string): Promise<boolean> {
  const p = new URLSearchParams(location.search);
  const code = p.get('code');
  if (!code) return false;

  const provider = sessionStorage.getItem(OAUTH_PROVIDER_KEY) ?? 'google';
  const isLinkMode = sessionStorage.getItem('corpllm_link_mode') === '1';
  history.replaceState({}, '', location.pathname + location.hash);
  sessionStorage.removeItem(OAUTH_PROVIDER_KEY);
  sessionStorage.removeItem('corpllm_link_mode');

  try {
    // Build headers — in link mode, include existing JWT for account linking
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (isLinkMode) {
      const existingJwt = getJwt();
      if (existingJwt) headers['Authorization'] = `Bearer ${existingJwt}`;
    }

    let res: Response;

    if (provider === 'google') {
      const verifier = sessionStorage.getItem(VERIFIER_KEY);
      if (!verifier) return false;
      sessionStorage.removeItem(VERIFIER_KEY);
      res = await fetch(`${serverBase}/auth/google`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ code, code_verifier: verifier, redirect_uri: location.origin }),
      });
    } else {
      // GitHub + Discord: same shape — just code + redirect_uri
      res = await fetch(`${serverBase}/auth/${provider}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ code, redirect_uri: location.origin }),
      });
    }

    if (!res.ok) return false;
    const { token } = await res.json() as { token: string };
    setJwt(token);
    return true;
  } catch { return false; }
}
